import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in to manage blogs.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/blogs`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setBlogs(response.data.blogs);
      } else {
        setError(response.data.message || 'Failed to fetch blogs.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching blogs.');
    } finally {
      setLoading(false);
    }
  };

  const handleFeaturedImageChange = (e) => {
    setFeaturedImage(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const formData = new FormData();
    formData.append('title', title);
    formData.append('summary', summary);
    formData.append('content', content);
    formData.append('featured_image', featuredImage);

    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('Authentication required.');
        return;
      }
      const response = await axios.post(`${apiUrl}/api/admin/blogs`, formData, {
        headers: {
          Authorization: `Bearer ${sessionData.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.success) {
        setSuccess('Blog added successfully.');
        setTitle('');
        setSummary('');
        setContent('');
        setFeaturedImage(null);
        fetchBlogs();
      } else {
        setError(response.data.message || 'Failed to add blog.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding blog.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
        const response = await axios.delete(`${apiUrl}/api/admin/blogs/${id}`, {
          headers: { Authorization: `Bearer ${sessionData.token}` }
        });
        if (response.data.success) {
          fetchBlogs();
        } else {
          setError(response.data.message || 'Failed to delete blog.');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting blog.');
      }
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link
                    to="/admin-dashboard"
                    className="text-gray-200 fw-normal text-15 hover-text-main-600"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Manage Blogs</span>
                </li>
              </ul>
            </div>
            <div className="flex-align gap-8 flex-wrap"></div>
          </div>
          <div className="card overflow-hidden">
            <div className="card-body p-24">
              <h4 className="mb-16">Add New Blog</h4>
              {error && <div className="alert alert-danger mb-16">{error}</div>}
              {success && <div className="alert alert-success mb-16">{success}</div>}
              <form onSubmit={handleSubmit} encType="multipart/form-data" className="mb-24">
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="summary" className="form-label">Summary</label>
                  <textarea
                    className="form-control"
                    id="summary"
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    required
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="content" className="form-label">Content</label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={content}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setContent(data);
                    }}
                    config={{
                      toolbar: [
                        'heading', '|',
                        'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                        'undo', 'redo'
                      ]
                    }}
                    style={{ minHeight: '300px' }}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="featured_image" className="form-label">Featured Image</label>
                  <input
                    type="file"
                    className="form-control"
                    id="featured_image"
                    accept="image/*"
                    onChange={handleFeaturedImageChange}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary">Add Blog</button>
              </form>
            </div>
            <div className="card-body p-0 overflow-x-auto">
              {loading && (
                <div className="alert alert-info mb-0" role="alert">
                  Loading blogs...
                </div>
              )}
              {error && (
                <div className="alert alert-danger mb-0" role="alert">
                  {error}
                </div>
              )}
              {!loading && !error && blogs.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  No blogs found.
                </div>
              )}
              {!loading && !error && blogs.length > 0 && (
                <table id="blogTable" className="table table-striped w-full">
                  <thead>
                    <tr>
                      <th className="fixed-width">
                        <div className="form-check">
                          <input
                            className="form-check-input border-gray-200 rounded-4"
                            type="checkbox"
                            id="selectAll"
                          />
                        </div>
                      </th>
                      <th className="h6 text-gray-300">Title</th>
                      <th className="h6 text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs.map((blog) => (
                      <tr key={blog.id}>
                        <td className="fixed-width">
                          <div className="form-check">
                            <input
                              className="form-check-input border-gray-200 rounded-4"
                              type="checkbox"
                            />
                          </div>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {blog.title}
                          </span>
                        </td>
                        <td>
                          <Link
                            to={`/admin-edit-blog/${blog.id}`}
                            className="bg-main-50 text-main-600 py-2 px-14 rounded-pill hover-bg-main-600 hover-text-white"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDelete(blog.id)}
                            className="bg-main-50 text-main-600 py-2 px-14 rounded-pill hover-bg-main-600 hover-text-white ml-2"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="card-footer flex-between flex-wrap">
              <span className="text-gray-900">
                Showing {blogs.length} of {blogs.length} entries
              </span>
              <ul className="pagination flex-align flex-wrap">
                <li className="page-item active">
                  <a
                    className="page-link h-44 w-44 flex-center text-15 rounded-8 fw-medium"
                    href="#"
                  >
                    1
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminBlogs;